/**
 * RichiesteAssicurativeTab — admin panel per gestione richieste CAT NAT
 * (e future tipologie RiS, ecc.) dalla collection richieste_assicurative.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, FileText, Phone, Mail, X, MessageSquarePlus, Lock, Unlock, Heart, Shield, FileDown, Eye, Download } from "lucide-react";
import RisPremiumCalculator from "@/components/admin/RisPremiumCalculator";
import VrsDetailBlock from "@/components/admin/VrsDetailBlock";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATI = [
  { value: "nuova", label: "Nuova", color: "bg-amber-100 text-amber-800" },
  { value: "in_lavorazione", label: "In lavorazione", color: "bg-blue-100 text-blue-800" },
  { value: "preventivo_inviato", label: "Preventivo inviato", color: "bg-emerald-100 text-emerald-800" },
  { value: "chiusa_vinta", label: "Chiusa vinta", color: "bg-green-600 text-white" },
  { value: "chiusa_persa", label: "Chiusa persa", color: "bg-red-100 text-red-800" },
];
const stateBadge = (s) => STATI.find(x => x.value === s) || STATI[0];

export default function RichiesteAssicurativeTab() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tipologia, setTipologia] = useState("");
  const [stato, setStato] = useState("");
  const [open, setOpen] = useState(null);
  const [newNote, setNewNote] = useState("");
  // Stato decrypt health
  const [decryptModal, setDecryptModal] = useState(null);  // {id, motivo, busy}
  const [healthData, setHealthData] = useState(null);  // dati sanitari decifrati per la richiesta open
  const [pdfModal, setPdfModal] = useState(null);  // {id, motivo, busy} per download PDF completo

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tipologia) params.set("tipologia", tipologia);
      if (stato) params.set("stato", stato);
      params.set("limit", "100");
      const { data } = await axios.get(`${API}/admin/richieste-assicurative?${params}`, { withCredentials: true });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error("Errore caricamento richieste");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tipologia, stato]);

  const changeStato = async (id, newStato) => {
    try {
      await axios.patch(`${API}/admin/richieste-assicurative/${id}`, { stato: newStato }, { withCredentials: true });
      toast.success(`Stato aggiornato → ${newStato}`);
      load();
      if (open?.id === id) setOpen({ ...open, stato: newStato });
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  const addNota = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await axios.post(`${API}/admin/richieste-assicurative/${open.id}/note`, { testo: newNote }, { withCredentials: true });
      setOpen({ ...open, note_admin: [...(open.note_admin || []), data.nota] });
      setNewNote("");
      toast.success("Nota aggiunta");
    } catch { toast.error("Errore aggiunta nota"); }
  };

  const requestDecrypt = () => {
    setHealthData(null);
    setDecryptModal({ id: open.id, motivo: "", busy: false });
  };

  const confirmDecrypt = async () => {
    const motivo = (decryptModal?.motivo || "").trim();
    if (motivo.length < 10) {
      toast.error("Inserisci un motivo di almeno 10 caratteri (audit log GDPR)");
      return;
    }
    setDecryptModal((m) => ({ ...m, busy: true }));
    try {
      const { data } = await axios.post(
        `${API}/admin/richieste-assicurative/${decryptModal.id}/decrypt-health-data`,
        { motivo },
        { withCredentials: true },
      );
      setHealthData(data);
      setDecryptModal(null);
      toast.success("Dati sanitari decifrati. Accesso registrato in audit log.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((x) => x.msg).join(" — ") : (detail || "Errore decifratura");
      toast.error(msg);
      setDecryptModal((m) => (m ? { ...m, busy: false } : null));
    }
  };

  // Reset health quando cambio richiesta selezionata
  useEffect(() => { setHealthData(null); }, [open?.id]);

  // === PDF Download ===
  const downloadPdfPreview = async () => {
    try {
      const res = await axios.get(`${API}/admin/richieste-assicurative/${open.id}/pdf-precompilato/preview`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success("Anteprima PDF aperta");
    } catch {
      toast.error("Errore download anteprima PDF");
    }
  };

  const requestPdfCompleto = () => {
    setPdfModal({ id: open.id, motivo: "", busy: false });
  };

  // === Mod. 5126 (RealmenteWellness) — flat PDF overlay, NO dati sanitari ===
  const downloadModulo5126Preview = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/richieste-assicurative/${open.id}/modulo-5126/preview-positions`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success("Preview Mod. 5126 aperta — verifica le coordinate (riquadri rossi)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore preview Mod. 5126");
    }
  };

  const downloadModulo5126 = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/richieste-assicurative/${open.id}/modulo-5126`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success("Mod. 5126 precompilato aperto. Firma manuale in agenzia.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore download Mod. 5126");
    }
  };

  // === Audit DPO export (CSV / PDF) — Fase 8 ===
  const _exportAudit = async (fmt) => {
    try {
      const res = await axios.get(
        `${API}/admin/audit/health-data-access/export`,
        { params: { fmt, days: 365 }, withCredentials: true, responseType: "blob" }
      );
      const blob = new Blob([res.data], {
        type: fmt === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_health_data_access_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const records = res.headers["x-audit-records"] || "?";
      toast.success(`Export ${fmt.toUpperCase()} completato (${records} record).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Errore export audit ${fmt}`);
    }
  };
  const exportAuditCsv = () => _exportAudit("csv");
  const exportAuditPdf = () => _exportAudit("pdf");

  const confirmPdfCompleto = async () => {
    const motivo = (pdfModal?.motivo || "").trim();
    if (motivo.length < 10) {
      toast.error("Inserisci un motivo di almeno 10 caratteri (audit log GDPR)");
      return;
    }
    setPdfModal((m) => ({ ...m, busy: true }));
    try {
      const res = await axios.get(
        `${API}/admin/richieste-assicurative/${pdfModal.id}/pdf-precompilato`,
        { params: { motivo }, withCredentials: true, responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const auditId = res.headers["x-audit-id"];
      setPdfModal(null);
      toast.success(`PDF completo aperto. Accesso registrato (audit: ${auditId?.slice(0, 8)}…).`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Errore download PDF completo");
      setPdfModal((m) => (m ? { ...m, busy: false } : null));
    }
  };

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="font-heading text-xl text-[#163321] mr-auto">Richieste Assicurative ({count})</h2>
        <select value={tipologia} onChange={e => setTipologia(e.target.value)} className="h-9 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
          <option value="">Tutte le tipologie</option><option value="catnat">CAT NAT</option><option value="rischi_salute">Rischi Salute</option>
        </select>
        <select value={stato} onChange={e => setStato(e.target.value)} className="h-9 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <Button size="sm" onClick={load} disabled={loading} variant="outline"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
        <Button size="sm" onClick={exportAuditCsv} variant="outline" className="text-purple-700 border-purple-200 hover:bg-purple-50" data-testid="audit-export-csv-btn" title="Export CSV log accessi dati sanitari (DPO)">
          <Download className="w-3.5 h-3.5 mr-1" /> Audit CSV
        </Button>
        <Button size="sm" onClick={exportAuditPdf} variant="outline" className="text-purple-700 border-purple-200 hover:bg-purple-50" data-testid="audit-export-pdf-btn" title="Export PDF log accessi dati sanitari (DPO)">
          <Download className="w-3.5 h-3.5 mr-1" /> Audit PDF
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[#4A5E51] py-10 text-center">Nessuna richiesta assicurativa.</p>
      ) : (
        <div className="border border-[#E5E2DB] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F4F0] text-[11px] uppercase tracking-wide text-[#4A5E51]">
              <tr>
                <th className="text-left p-2">Data</th><th className="text-left p-2">Tipo</th><th className="text-left p-2">Impresa</th>
                <th className="text-left p-2">Contatti</th><th className="text-left p-2">Stato</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const b = stateBadge(r.stato);
                return (
                  <tr key={r.id} className="border-t border-[#E5E2DB]" data-testid={`richiesta-row-${r.id}`}>
                    <td className="p-2 text-[11px] text-[#4A5E51]">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                    <td className="p-2 font-semibold">{r.tipologia.toUpperCase()}</td>
                    <td className="p-2"><strong>{r.cliente?.ragione_sociale}</strong><br/><span className="text-[10px] text-[#4A5E51]">P.IVA {r.cliente?.partita_iva}</span></td>
                    <td className="p-2 text-[11px]"><a href={`mailto:${r.cliente?.email}`} className="text-[#163321] hover:underline">{r.cliente?.email}</a><br/><a href={`tel:${r.cliente?.telefono}`} className="text-[#4A5E51]">{r.cliente?.telefono}</a></td>
                    <td className="p-2"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${b.color}`}>{b.label}</span></td>
                    <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => setOpen(r)}><FileText className="w-3.5 h-3.5 mr-1" />Dettagli</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="richiesta-detail">
            <div className="sticky top-0 bg-white border-b border-[#E5E2DB] px-5 py-3 flex items-center justify-between">
              <h3 className="font-heading text-lg text-[#163321]">Richiesta {open.tipologia.toUpperCase()} — {open.cliente?.ragione_sociale}</h3>
              <button onClick={() => setOpen(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="flex flex-wrap gap-2">
                {STATI.map(s => (
                  <button key={s.value} onClick={() => changeStato(open.id, s.value)} className={`text-xs px-3 py-1 rounded-full font-semibold ${open.stato === s.value ? s.color : "bg-[#F5F4F0] text-[#4A5E51] hover:bg-[#E5E2DB]"}`}>{s.label}</button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
                <div><strong>P.IVA:</strong> {open.cliente?.partita_iva}</div>
                <div><strong>ATECO:</strong> {open.cliente?.codice_ateco}</div>
                <div><strong>Forma:</strong> {open.cliente?.forma_giuridica}</div>
                <div><strong>Fatturato:</strong> € {(open.cliente?.fatturato_annuo || 0).toLocaleString("it-IT")}</div>
                <div><strong>Dipendenti:</strong> {open.cliente?.numero_dipendenti}</div>
                <div><strong>Referente:</strong> {open.cliente?.referente?.nome} {open.cliente?.referente?.cognome} ({open.cliente?.referente?.ruolo})</div>
              </div>
              {open.tipologia === "catnat" && open.dati_specifici && (
                <div className="bg-[#F5F4F0] rounded-xl p-3">
                  <p className="text-[11px] uppercase tracking-wide font-bold text-[#4A5E51] mb-2">Ubicazione</p>
                  <p>{open.dati_specifici.ubicazione?.indirizzo}, {open.dati_specifici.ubicazione?.cap} {open.dati_specifici.ubicazione?.citta} ({open.dati_specifici.ubicazione?.provincia})</p>
                  <p className="text-[10px] text-[#4A5E51] mt-1">Sisma: {open.dati_specifici.ubicazione?.zona_sismica_dichiarata} — Idro: {open.dati_specifici.ubicazione?.area_rischio_idrogeologico_dichiarata}</p>
                  <p className="text-[11px] uppercase tracking-wide font-bold text-[#4A5E51] mt-3 mb-1">Garanzie richieste</p>
                  <ul className="text-[12px] space-y-0.5">
                    {Object.entries(open.dati_specifici.garanzie_cat_richieste || {}).filter(([, g]) => g.attiva).map(([k, g]) => (
                      <li key={k}>• <strong>{k}</strong> — scoperto {g.scoperto_pct}% (min € {g.scoperto_minimo_eur.toLocaleString("it-IT")}), limite {g.limite_indennizzo_pct}%</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stima valore di ricostruzione (silente, FASE 10C.2) — visibile solo admin */}
              {open.tipologia === "catnat" && (
                <VrsDetailBlock
                  data={open}
                  onRicalcola={async () => {
                    try {
                      const { data: r } = await axios.post(
                        `${API}/admin/catnat/${open.id}/ricalcola-vrs`,
                        {},
                        { withCredentials: true }
                      );
                      setOpen({
                        ...open,
                        valore_ricostruzione_stimato: r.valore_ricostruzione_stimato,
                        valore_ricostruzione_audit: r.audit,
                      });
                      toast.success(r.outcome === "ok" ? "Stima aggiornata" : `Skip: ${r.reason}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Errore ricalcolo VRS");
                    }
                  }}
                />
              )}

              {/* Sezione RiS — dati sanitari cifrati */}
              {open.tipologia === "rischi_salute" && (
                <div className="bg-[#F5F4F0] rounded-xl p-3 space-y-2">
                  <p className="text-[11px] uppercase tracking-wide font-bold text-[#4A5E51] mb-1 flex items-center gap-1.5">
                    <Heart className="w-3 h-3" /> Configurazione polizza
                  </p>
                  {open.dati_specifici_ris?.configurazione && (
                    <div className="text-[12px]">
                      Prodotto: <strong>{open.dati_specifici_ris.configurazione.prodotto === "vita_intera" ? "Vita Intera" : `Durata Definita ${open.dati_specifici_ris.configurazione.durata_anni || "?"} anni`}</strong>
                      {" · "}Opzione <strong>{open.dati_specifici_ris.configurazione.opzione}</strong>
                      {open.dati_specifici_ris.configurazione.garanzie_extra?.length > 0 && (
                        <span> · Extra: {open.dati_specifici_ris.configurazione.garanzie_extra.join(", ")}</span>
                      )}
                      <span> · Frazionamento: {open.dati_specifici_ris.configurazione.frazionamento}</span>
                    </div>
                  )}
                  {open.dati_specifici_ris?.nucleo && (
                    <div className="text-[12px]">Nucleo: <strong>{open.dati_specifici_ris.nucleo.composizione}</strong>{open.dati_specifici_ris.nucleo.figli?.length > 0 && <> · {open.dati_specifici_ris.nucleo.figli.length} figli</>}</div>
                  )}

                  <div className="border-t border-[#E5E2DB] pt-2 mt-2">
                    <p className="text-[11px] uppercase tracking-wide font-bold text-[#4A5E51] mb-1.5 flex items-center gap-1.5">
                      <Shield className="w-3 h-3" /> Dati sanitari (art. 9 GDPR)
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] mb-2">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-semibold">
                        🔒 AES-256-GCM
                      </span>
                      {open.dati_sanitari_meta?.ha_patologie_dichiarate ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                          ⚠ Patologie dichiarate
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                          ✓ No patologie
                        </span>
                      )}
                      {open.dati_sanitari_meta?.fumatore && (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-800">Fumatore</span>
                      )}
                      {open.dati_sanitari_meta?.sport_agonistici_o_alto_rischio && (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-800">Sport alto rischio</span>
                      )}
                    </div>

                    {!healthData ? (
                      <Button
                        size="sm"
                        onClick={requestDecrypt}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="ris-decrypt-btn"
                      >
                        <Unlock className="w-3.5 h-3.5 mr-1" /> Decripta dati sanitari
                      </Button>
                    ) : (
                      <div className="bg-white border border-purple-200 rounded-xl p-3 text-[12px]" data-testid="ris-health-data-decrypted">
                        <div className="flex items-center gap-2 mb-2">
                          <Lock className="w-3.5 h-3.5 text-purple-600" />
                          <span className="text-[10px] font-semibold text-purple-800 uppercase tracking-wide">Decifrato (audit ID: {healthData.audit_id?.slice(0, 8)}…)</span>
                        </div>
                        <pre className="whitespace-pre-wrap break-words text-[11px] text-[#0F1F14] font-mono bg-[#F5F4F0] rounded p-2">
{JSON.stringify(healthData.dati_sanitari, null, 2)}
                        </pre>
                        {healthData.diritto_oblio_oncologico && (
                          <p className="text-[10px] italic text-[#4A5E51] mt-2">{healthData.diritto_oblio_oncologico}</p>
                        )}
                      </div>
                    )}

                    {/* PDF download buttons */}
                    <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-[#E5E2DB]">
                      <Button size="sm" variant="outline" onClick={downloadPdfPreview} data-testid="ris-pdf-preview-btn">
                        <Eye className="w-3.5 h-3.5 mr-1" /> Anteprima Quest. 5076
                      </Button>
                      <Button size="sm" onClick={requestPdfCompleto} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ris-pdf-completo-btn">
                        <FileDown className="w-3.5 h-3.5 mr-1" /> PDF completo (5076)
                      </Button>
                      <Button size="sm" variant="outline" onClick={downloadModulo5126Preview} data-testid="ris-mod5126-preview-btn" title="Anteprima posizioni Mod. 5126 con riquadri rossi">
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview Mod. 5126
                      </Button>
                      <Button size="sm" onClick={downloadModulo5126} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ris-mod5126-btn">
                        <FileDown className="w-3.5 h-3.5 mr-1" /> Mod. 5126 (RealmenteWellness)
                      </Button>
                    </div>
                  </div>

                  {/* Fase 9B — Quotazione Premio (admin-only) */}
                  <div className="mt-3">
                    <RisPremiumCalculator richiesta={open} onUpdated={load} />
                  </div>
                </div>
              )}
              {open.note_cliente && <div className="bg-amber-50 rounded-xl p-3"><strong>Note cliente:</strong> {open.note_cliente}</div>}
              <div className="border-t border-[#E5E2DB] pt-3">
                <p className="text-[11px] uppercase tracking-wide font-bold text-[#4A5E51] mb-2">Note admin</p>
                <div className="space-y-1.5 mb-2">
                  {(open.note_admin || []).map((n, i) => (
                    <div key={i} className="bg-[#F5F4F0] rounded-xl p-2 text-[12px]">
                      <span className="text-[10px] text-[#4A5E51]">{new Date(n.ts).toLocaleString("it-IT")} — {n.autore}</span>
                      <p>{n.testo}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Aggiungi nota…" className="flex-1 h-9 px-3 border border-[#E5E2DB] rounded-md text-sm" />
                  <Button size="sm" onClick={addNota} disabled={!newNote.trim()}><MessageSquarePlus className="w-3.5 h-3.5 mr-1" />Salva</Button>
                </div>
              </div>
              <div className="flex gap-2 pt-3 border-t border-[#E5E2DB]">
                <a href={`tel:${open.cliente?.telefono}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700"><Phone className="w-3.5 h-3.5" />Chiama</a>
                <a href={`mailto:${open.cliente?.email}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#163321] text-white text-xs hover:bg-[#0F2F1F]"><Mail className="w-3.5 h-3.5" />Email</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE: motivo decrypt dati sanitari */}
      {decryptModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => !decryptModal.busy && setDecryptModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="ris-decrypt-modal">
            <div className="flex items-center gap-2 mb-3">
              <Unlock className="w-5 h-5 text-purple-600" />
              <h3 className="font-heading text-lg text-[#0F1F14]">Decifratura dati sanitari</h3>
            </div>
            <p className="text-xs text-[#4A5E51] mb-3 leading-relaxed">
              Stai per accedere a dati particolari ex art. 9 GDPR. Questo accesso verrà <strong>registrato in audit log immutabile</strong> (TTL 5 anni). Indica il motivo (min. 10 caratteri).
            </p>
            <textarea
              value={decryptModal.motivo}
              onChange={(e) => setDecryptModal({ ...decryptModal, motivo: e.target.value })}
              placeholder="Es: Preparazione preventivo polizza n. 12345 - valutazione rischio cardiovascolare"
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 border border-[#E5E2DB] rounded-md text-sm focus:outline-none focus:border-[#163321] mb-3"
              data-testid="ris-decrypt-motivo"
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setDecryptModal(null)} disabled={decryptModal.busy}>
                Annulla
              </Button>
              <Button
                size="sm"
                onClick={confirmDecrypt}
                disabled={decryptModal.busy || (decryptModal.motivo || "").trim().length < 10}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="ris-decrypt-confirm"
              >
                <Unlock className="w-3.5 h-3.5 mr-1" /> {decryptModal.busy ? "Decifratura…" : "Decripta e logga"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* MODALE: motivo download PDF completo */}
      {pdfModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => !pdfModal.busy && setPdfModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="ris-pdf-modal">
            <div className="flex items-center gap-2 mb-3">
              <FileDown className="w-5 h-5 text-emerald-600" />
              <h3 className="font-heading text-lg text-[#0F1F14]">Download PDF completo</h3>
            </div>
            <p className="text-xs text-[#4A5E51] mb-3 leading-relaxed">
              Stai per scaricare il <strong>questionario sanitario Mod. 5076 Reale Mutua</strong> precompilato con tutti i dati (anagrafica + dati sanitari art. 9 GDPR).
              Questo accesso verrà <strong>registrato in audit log immutabile</strong>. Indica il motivo (min. 10 caratteri).
            </p>
            <textarea
              value={pdfModal.motivo}
              onChange={(e) => setPdfModal({ ...pdfModal, motivo: e.target.value })}
              placeholder="Es: Preparazione preventivo polizza n. 12345 - invio modulo cartaceo cliente"
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 border border-[#E5E2DB] rounded-md text-sm focus:outline-none focus:border-[#163321] mb-3"
              data-testid="ris-pdf-motivo"
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setPdfModal(null)} disabled={pdfModal.busy}>
                Annulla
              </Button>
              <Button
                size="sm"
                onClick={confirmPdfCompleto}
                disabled={pdfModal.busy || (pdfModal.motivo || "").trim().length < 10}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ris-pdf-confirm"
              >
                <FileDown className="w-3.5 h-3.5 mr-1" /> {pdfModal.busy ? "Generazione…" : "Scarica PDF e logga"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
